// PolicyChip — a policy tier, everywhere it appears across the Fleet page and
// its modals. Used to be several different treatments of the same fact (a
// plain link with a shield icon in one place, a static purple tag in
// another, a different static tag in the Deploy SSH sandbox modal's grant
// list). One chip now, shared. Deliberately NOT a filled purple Tag —
// purple/preset-color fill is CLAUDE.md's convention for a *categorical*
// label (severity, chart series), and this isn't one; it's a reference to
// another object, clickable through to that object's detail. A neutral
// outlined pill with a hover fill reads as "reference, click me" without
// borrowing the categorical-color meaning. Leading shield icon (what tier
// this is), not a trailing arrow (that it's a link) — the arrow was extra
// width earning its keep only as chrome, and a header row of 2-3 chips was
// wrapping under it at moderate widths.
//
// The hover state is real inline styling driven by useToken(), not a
// Tailwind hover: class — a background-tint-only hover on a near-white
// card read as barely-there. Hovering now shifts border AND text/icon
// color to colorPrimary (text via `color`, inherited by the icon through
// currentColor) plus a visible fill, the same "this is now live" signal
// as a standard link hover, not just a faint tint.
import { useState } from "react";
import { theme } from "antd";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Tag } from "@/shared/ui";

export function PolicyChip({ tier, linkable = true }: { tier: string; linkable?: boolean }) {
  const { token } = theme.useToken();
  const [hovered, setHovered] = useState(false);
  const chip = (
    <Tag
      variant="outlined"
      mono
      // className, not style — AntD Tag's cloneElement on `icon` replaces
      // style entirely but merges className (see the same note on the
      // Host/Management Host tags in HostCard.tsx).
      icon={<ShieldCheck size={11} className="inline-block align-middle mr-1" />}
      style={{
        margin: 0,
        cursor: linkable ? "pointer" : "default",
        transition: "background-color 0.15s, border-color 0.15s, color 0.15s",
        ...(linkable && hovered
          ? { backgroundColor: token.colorPrimaryBg, borderColor: token.colorPrimary, color: token.colorPrimary }
          : {}),
      }}
    >
      {tier}
    </Tag>
  );
  // linkable: false — e.g. ConnectModal's title row, where the chip is just
  // "here's the policy this shares", not another navigation affordance
  // competing with the modal's own actions.
  if (!linkable) return chip;
  return (
    <Link
      to={`/policy?policy=${encodeURIComponent(tier)}`}
      title={`Open the ${tier} policy`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {chip}
    </Link>
  );
}

export default PolicyChip;
